import { RouteComponentProps, useLocation } from '@reach/router';
import { ResourcesApi } from 'api/resourcesApi';
import EqualHeightsList from 'components/resources/EqualHeightsList';
import ResourceBankPage from 'components/resources/ResourceBankPage';
import { ResourceCards } from 'components/resources/ResourceList';
import ResourcePageStatus from 'components/resources/ResourcePageStatus';
import {
  awesomeStuffPath,
  byName,
  isPageData,
  ResourcesRouteParams,
  SEARCH_RESOURCE_TYPES,
  tilePath,
  usePageTitle,
  useResourcesPage,
} from 'components/resources/resourcesHelpers';
import ResourceTile from 'components/resources/ResourceTile';
import SubjectSiteLayout from 'components/subjectSite/SubjectSiteLayout';
import SearchImage from 'images/subjectSite/OAA.png';
import queryString from 'query-string';
import React, { useRef } from 'react';

type Props = RouteComponentProps<ResourcesRouteParams>;

/** Search results show on the resource bank's colour, not their category's */
const RESULT_COLOUR = 'Blue_GSFPE';

const ResourceSearch: React.FC<Props> = ({ subject: subjectCode }) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const { search } = useLocation();
  const searchParam = queryString.parse(search).searchString;
  const searchTerm = typeof searchParam === 'string' ? searchParam.trim() : '';
  const result = useResourcesPage(
    subjectCode,
    searchTerm
      ? (subjectsId) => ResourcesApi.search(subjectsId, searchTerm)
      : null,
    searchTerm
  );
  usePageTitle(
    isPageData(result) ? `${result.subject.name} - Resource Bank` : null
  );

  if (!isPageData(result)) return <ResourcePageStatus result={result} />;

  const { subject, details } = result;
  const code = subject.internalCode;
  const groups = SEARCH_RESOURCE_TYPES.map((resourceType) =>
    byName(
      details.resources.filter(
        (resource) => resource.resourceType === resourceType
      )
    )
  ).filter((resources) => resources.length > 0);

  return (
    <SubjectSiteLayout colorCss="Blue_GSFPE">
      <ResourceBankPage
        ref={pageRef}
        subjectCode={code}
        title="Search results for..."
        subtitle={`"${searchTerm}"`}
        imageSrc={SearchImage}
        backPath={awesomeStuffPath(code)}
      >
        {groups.length === 0 && <p>No resources found.</p>}
        {groups.map((resources) => (
          <React.Fragment key={resources[0].resourceType}>
            <div className="LineSpacer6" />
            <ResourceCards
              subjectCode={code}
              subjectsId={subject.subjectsID}
              resources={resources}
              colorCSS={RESULT_COLOUR}
              pageRef={pageRef}
              bCentred
              bSearchResults
            />
          </React.Fragment>
        ))}
        {(groups.length > 0 || details.tiles.length > 0) && (
          <>
            <div className="LineSpacer6" />
            <div className="VirtualPECupboard">
              <EqualHeightsList
                className="WarpperActivity WrapperList01"
                cardsSource={details}
                pageRef={pageRef}
              >
                {details.tiles.map((tile) => (
                  <React.Fragment key={`${tile.tileType}-${tile.id}`}>
                    {' '}
                    <ResourceTile
                      className={
                        tile.tileType === 'ResourceCategory'
                          ? 'Item ItemList01 FixedColour'
                          : 'Item ItemList01'
                      }
                      colorCSS={tile.colorCSS}
                      name={tile.name}
                      imageSrc={tile.imagePath}
                      imageAlt={tile.name}
                      to={tilePath(code, tile.id, tile.tileType)}
                    />
                  </React.Fragment>
                ))}
              </EqualHeightsList>
            </div>
          </>
        )}
      </ResourceBankPage>
    </SubjectSiteLayout>
  );
};

export default ResourceSearch;
